import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  listProducts,
  getProduct,
  createProduct,
  updateProduct,
  deleteProduct,
  bulkUpdateProducts,
  importProducts,
  getProductItemGroup,
  listAttachments,
  createAttachment,
  updateAttachment,
  deleteAttachment,
  importAttachments,
  listCategories,
  getCategoryDetail,
  listProductGroups,
  getProductGroup,
  createProductGroup,
  updateProductGroup,
  deleteProductGroup,
  uploadProductGroupImages,
  deleteProductGroupImage,
  moveProductGroupImage,
  listTags,
  get2020Catalog,
  update2020Catalog,
} from "../api/catalog-api";
import {
  listConstructionMethods,
  createConstructionMethod,
  updateConstructionMethod,
  deleteConstructionMethod,
  deleteConstructionMethodImage,
  createPremium,
  updatePremium,
  deletePremium,
  importPremiums,
  listPremiumGroups,
  updatePremiumGroupIndex,
} from "../api/premium-api";
import {
  listPublicCategories,
  getPublicCategory,
  createPublicCategory,
  updatePublicCategory,
  deletePublicCategory,
  moveCategory,
  listPublicSubCategories,
  getPublicSubCategory,
  createPublicSubCategory,
  updatePublicSubCategory,
  deletePublicSubCategory,
  moveSubCategory,
  moveGroup,
  addGroupToSubCategory,
  removeGroupFromSubCategory,
  browseCatalog,
} from "../api/public-catalog-api";
import type { CatalogListParams } from "../types/catalog.types";

// ─── Products ────────────────────────────────────────────

export function useProducts(params: CatalogListParams, enabled = true) {
  return useQuery({
    queryKey: ["products", params],
    queryFn: () => listProducts(params as Record<string, unknown>),
    enabled,
  });
}

export function useCreateProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) => createProduct(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["products"] }),
  });
}

export function useUpdateProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: Record<string, unknown> }) =>
      updateProduct(id, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["products"] }),
  });
}

export function useDeleteProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteProduct(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["products"] }),
  });
}

export function useBulkUpdateProducts() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ids, values }: { ids: number[]; values: Record<string, unknown> }) =>
      bulkUpdateProducts(ids, values),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["products"] }),
  });
}

export function useImportProducts() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: FormData) => importProducts(file),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["products"] }),
  });
}

export function useProduct(id: number | null) {
  return useQuery({
    queryKey: ["product", id],
    queryFn: () => getProduct(id!),
    enabled: !!id,
  });
}

export function useProductItemGroup(itemId: number | null) {
  return useQuery({
    queryKey: ["product-item-group", itemId],
    queryFn: () => getProductItemGroup(itemId!),
    enabled: !!itemId,
  });
}

export function useTags() {
  return useQuery({
    queryKey: ["tags"],
    queryFn: () => listTags(),
  });
}

// ─── Attachments ─────────────────────────────────────────

export function useAttachments(params: CatalogListParams, enabled = true) {
  return useQuery({
    queryKey: ["attachments", params],
    queryFn: () => listAttachments(params as Record<string, unknown>),
    enabled,
  });
}

export function useCreateAttachment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) => createAttachment(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["attachments"] }),
  });
}

export function useUpdateAttachment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: Record<string, unknown> }) =>
      updateAttachment(id, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["attachments"] }),
  });
}

export function useDeleteAttachment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteAttachment(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["attachments"] }),
  });
}

export function useImportAttachments() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: FormData) => importAttachments(file),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["attachments"] }),
  });
}

// ─── Categories (staff) ──────────────────────────────────

export function useCategories(params: Record<string, unknown> = {}) {
  return useQuery({
    queryKey: ["categories", params],
    queryFn: () => listCategories(params),
  });
}

export function useCategory(id: number | null) {
  return useQuery({
    queryKey: ["category", id],
    queryFn: () => getCategoryDetail(id!),
    enabled: !!id,
  });
}

// ─── Product Groups ──────────────────────────────────────

export function useProductGroups(params: Record<string, unknown>) {
  return useQuery({
    queryKey: ["product-groups", params],
    queryFn: () => listProductGroups(params),
  });
}

export function useProductGroup(id: number | null) {
  return useQuery({
    queryKey: ["product-group", id],
    queryFn: () => getProductGroup(id!),
    enabled: !!id,
  });
}

export function useCreateProductGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: FormData) => createProductGroup(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["product-groups"] }),
  });
}

export function useUpdateProductGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: Record<string, unknown> }) =>
      updateProductGroup(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["product-groups"] });
      qc.invalidateQueries({ queryKey: ["product-group"] });
    },
  });
}

export function useDeleteProductGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteProductGroup(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["product-groups"] }),
  });
}

export function useUploadProductGroupImages() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, images }: { id: number; images: FormData }) =>
      uploadProductGroupImages(id, images),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["product-groups"] });
      qc.invalidateQueries({ queryKey: ["product-group"] });
    },
  });
}

export function useDeleteProductGroupImage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (imageId: number) => deleteProductGroupImage(imageId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["product-groups"] });
      qc.invalidateQueries({ queryKey: ["product-group"] });
    },
  });
}

export function useMoveProductGroupImage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ groupId, payload }: { groupId: number; payload: Record<string, unknown> }) =>
      moveProductGroupImage(groupId, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["product-group"] }),
  });
}

// ─── Premiums (generic CRUD) ─────────────────────────────

export function useCreatePremium(type: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) => createPremium(type, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["premiums", type] }),
  });
}

export function useUpdatePremium(type: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: Record<string, unknown> }) =>
      updatePremium(type, id, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["premiums", type] }),
  });
}

export function useDeletePremium(type: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deletePremium(type, id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["premiums", type] }),
  });
}

export function useImportPremiums(type: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: FormData) => importPremiums(type, file),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["premiums", type] }),
  });
}

export function usePremiumGroups(premiumType: string) {
  return useQuery({
    queryKey: ["premium-groups", premiumType],
    queryFn: () => listPremiumGroups(premiumType),
  });
}

export function useUpdatePremiumGroupIndex() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ groupId, newIndex }: { groupId: number; newIndex: number }) =>
      updatePremiumGroupIndex(groupId, newIndex),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["premium-groups"] }),
  });
}

// ─── Construction Methods ────────────────────────────────

export function useConstructionMethods(params: Record<string, unknown>) {
  return useQuery({
    queryKey: ["construction-methods", params],
    queryFn: () => listConstructionMethods(params),
  });
}

export function useCreateConstructionMethod() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: FormData) => createConstructionMethod(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["construction-methods"] }),
  });
}

export function useUpdateConstructionMethod() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: FormData }) =>
      updateConstructionMethod(id, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["construction-methods"] }),
  });
}

export function useDeleteConstructionMethod() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteConstructionMethod(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["construction-methods"] }),
  });
}

export function useDeleteConstructionMethodImage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, imageId }: { id: number; imageId: number }) =>
      deleteConstructionMethodImage(id, imageId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["construction-methods"] }),
  });
}

// ─── Public Catalog ──────────────────────────────────────

export function usePublicCategories() {
  return useQuery({
    queryKey: ["public-categories"],
    queryFn: () => listPublicCategories(),
  });
}

export function usePublicCategory(id: number) {
  return useQuery({
    queryKey: ["public-category", id],
    queryFn: () => getPublicCategory(id),
    enabled: id > 0,
  });
}

export function useCreatePublicCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) => createPublicCategory(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["public-categories"] }),
  });
}

export function useUpdatePublicCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: Record<string, unknown> }) =>
      updatePublicCategory(id, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["public-categories"] }),
  });
}

export function useDeletePublicCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deletePublicCategory(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["public-categories"] }),
  });
}

export function useMoveCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: Record<string, unknown> }) =>
      moveCategory(id, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["public-categories"] }),
  });
}

export function usePublicSubCategories(params?: Record<string, unknown>) {
  return useQuery({
    queryKey: ["public-subcategories", params],
    queryFn: () => listPublicSubCategories(params),
  });
}

export function usePublicSubCategory(id: number) {
  return useQuery({
    queryKey: ["public-subcategory", id],
    queryFn: () => getPublicSubCategory(id),
    enabled: id > 0,
  });
}

export function useCreatePublicSubCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) => createPublicSubCategory(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["public-subcategories"] });
      qc.invalidateQueries({ queryKey: ["public-categories"] });
    },
  });
}

export function useUpdatePublicSubCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: Record<string, unknown> }) =>
      updatePublicSubCategory(id, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["public-subcategories"] }),
  });
}

export function useDeletePublicSubCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deletePublicSubCategory(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["public-subcategories"] });
      qc.invalidateQueries({ queryKey: ["public-categories"] });
    },
  });
}

export function useMoveSubCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ categoryId, payload }: { categoryId: number; payload: Record<string, unknown> }) =>
      moveSubCategory(categoryId, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["public-category"] }),
  });
}

export function useMoveGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ subCategoryId, payload }: { subCategoryId: number; payload: Record<string, unknown> }) =>
      moveGroup(subCategoryId, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["public-subcategory"] }),
  });
}

export function useAddGroupToSubCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ subCategoryId, payload }: { subCategoryId: number; payload: Record<string, unknown> }) =>
      addGroupToSubCategory(subCategoryId, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["public-subcategory"] }),
  });
}

export function useRemoveGroupFromSubCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ subCategoryId, payload }: { subCategoryId: number; payload: Record<string, unknown> }) =>
      removeGroupFromSubCategory(subCategoryId, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["public-subcategory"] }),
  });
}

export function useBrowseCatalog() {
  return useQuery({
    queryKey: ["browse-catalog"],
    queryFn: browseCatalog,
  });
}

// ─── 2020 Catalog ────────────────────────────────────────

export function use2020Catalog() {
  return useQuery({
    queryKey: ["2020-catalog"],
    queryFn: get2020Catalog,
  });
}

export function useUpdate2020Catalog() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: FormData) => update2020Catalog(file),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["2020-catalog"] }),
  });
}
